/**
 * AIInsightsCard - AI-powered fitness insights and recommendations
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Star,
  Target,
  Flame,
  Zap,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  MessageSquare,
  Clock,
  History
} from 'lucide-react';
import { supabase, fitness, profiles, getCachedUser } from '../../services/supabaseService';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

const AIInsightsCard = ({ workouts, stats, dateRange }) => {
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastAnalysisDate, setLastAnalysisDate] = useState(null);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(true);

  // Icon mapping
  const iconMap = {
    'trending-up': TrendingUp,
    'trending-down': TrendingDown,
    'alert': AlertTriangle,
    'check': CheckCircle,
    'star': Star,
    'target': Target,
    'flame': Flame,
    'zap': Zap,
  };

  // Priority colors
  const priorityColors = {
    high: 'border-red-500/50 bg-red-500/10',
    medium: 'border-yellow-500/50 bg-yellow-500/10',
    low: 'border-green-500/50 bg-green-500/10',
  };

  // Icon colors by type
  const typeColors = {
    trend: 'text-blue-400',
    balance: 'text-purple-400',
    consistency: 'text-orange-400',
    plateau: 'text-yellow-400',
    achievement: 'text-green-400',
    progression: 'text-cyan-400',
    technique: 'text-pink-400',
    intensity: 'text-red-400',
  };

  // Load previous analysis on mount or when dateRange changes
  useEffect(() => {
    const loadPreviousAnalysis = async () => {
      setIsLoadingPrevious(true);
      try {
        const savedAnalysis = await fitness.getLatestAnalysis(dateRange);
        if (savedAnalysis) {
          setInsights({
            summary: savedAnalysis.summary,
            insights: savedAnalysis.insights,
            recommendations: savedAnalysis.recommendations,
            technicalNotes: savedAnalysis.technical_notes,
            motivationalMessage: savedAnalysis.motivational_message,
          });
          setLastAnalysisDate(savedAnalysis.created_at);
        } else {
          setInsights(null);
          setLastAnalysisDate(null);
        }
      } catch (err) {
        console.error('Failed to load previous analysis:', err);
      } finally {
        setIsLoadingPrevious(false);
      }
    };

    loadPreviousAnalysis();
  }, [dateRange]);

  // Fetch AI insights
  const fetchInsights = async () => {
    if (!workouts?.length || !stats) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get user profile for personalized analysis
      let userProfile = null;
      try {
        const user = await getCachedUser();
        if (user) {
          const profile = await profiles.get(user.id);
          if (profile?.height_cm || profile?.weight_kg) {
            userProfile = {
              height_cm: profile.height_cm,
              weight_kg: profile.weight_kg,
            };
          }
        }
      } catch (profileErr) {
        console.warn('Could not load user profile for AI analysis:', profileErr);
      }

      const { data, error: fnError } = await supabase.functions.invoke('gemini', {
        body: {
          action: 'analyze-fitness-progress',
          payload: {
            workouts: workouts.slice(0, 500), // Limit data sent
            stats,
            dateRange,
            userProfile,
          },
        },
      });

      if (fnError) throw fnError;
      setInsights(data);

      // Save analysis to history
      try {
        await fitness.saveAnalysis(data, stats, dateRange);
        setLastAnalysisDate(new Date().toISOString());
      } catch (saveErr) {
        console.error('Failed to save analysis:', saveErr);
      }
    } catch (err) {
      console.error('AI analysis error:', err);
      setError('AI analizi yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              AI Antrenman Analizi
              {isLoading && (
                <RefreshCw className="w-4 h-4 animate-spin text-purple-400" />
              )}
            </h3>
            <p className="text-xs text-gray-400 flex items-center gap-1">
              {lastAnalysisDate ? (
                <>
                  <Clock className="w-3 h-3" />
                  {formatDistanceToNow(new Date(lastAnalysisDate), { addSuffix: true, locale: tr })}
                </>
              ) : (
                'Henüz analiz yapılmadı'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchInsights();
            }}
            disabled={isLoading}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Yenile"
          >
            <RefreshCw className={`w-4 h-4 text-gray-400 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4">
              {/* Loading State */}
              {(isLoading || isLoadingPrevious) && !insights && (
                <div className="flex flex-col items-center justify-center py-8">
                  <Sparkles className="w-8 h-8 text-purple-400 animate-pulse mb-3" />
                  <p className="text-gray-400">
                    {isLoadingPrevious ? 'Önceki analiz yükleniyor...' : 'AI verilerini analiz ediyor...'}
                  </p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                  <p className="text-red-400">{error}</p>
                  <button
                    onClick={fetchInsights}
                    className="mt-2 text-sm text-red-300 hover:text-red-200"
                  >
                    Tekrar dene
                  </button>
                </div>
              )}

              {/* Insights Content */}
              {insights && !isLoading && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-200">{insights.summary}</p>
                    </div>
                  </div>

                  {/* Insights Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {insights.insights?.map((insight, idx) => {
                      const IconComponent = iconMap[insight.icon] || Sparkles;
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={`
                            border rounded-lg p-3
                            ${priorityColors[insight.priority] || 'border-gray-700 bg-gray-800/50'}
                          `}
                        >
                          <div className="flex items-start gap-3">
                            <IconComponent
                              className={`w-5 h-5 mt-0.5 flex-shrink-0 ${typeColors[insight.type] || 'text-gray-400'}`}
                            />
                            <div>
                              <h4 className="font-medium text-white text-sm">
                                {insight.title}
                              </h4>
                              <p className="text-xs text-gray-400 mt-1">
                                {insight.description}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Recommendations */}
                  {insights.recommendations?.length > 0 && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb className="w-4 h-4 text-yellow-400" />
                        <h4 className="font-medium text-white text-sm">Öneriler</h4>
                      </div>
                      <ul className="space-y-2">
                        {insights.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <span className="text-purple-400 mt-1">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Technical Notes */}
                  {insights.technicalNotes?.length > 0 && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-cyan-400" />
                        <h4 className="font-medium text-white text-sm">Teknik Notlar</h4>
                      </div>
                      <ul className="space-y-2">
                        {insights.technicalNotes.map((note, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <span className="text-cyan-400 mt-1">•</span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Motivational Message */}
                  {insights.motivationalMessage && (
                    <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-500/30 rounded-lg p-4 text-center">
                      <p className="text-orange-200 font-medium">
                        💪 {insights.motivationalMessage}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Start Analysis State */}
              {!isLoading && !isLoadingPrevious && !error && !insights && (
                <div className="text-center py-8">
                  <Sparkles className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                  <p className="text-gray-300 mb-4">
                    AI ile antrenman verilerini analiz et
                  </p>
                  <button
                    onClick={fetchInsights}
                    disabled={!workouts?.length || !stats}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
                  >
                    <Sparkles className="w-5 h-5" />
                    Analizi Başlat
                  </button>
                  {(!workouts?.length || !stats) && (
                    <p className="text-xs text-gray-500 mt-2">
                      Analiz için yeterli veri yok
                    </p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AIInsightsCard;
